import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { List } from 'immutable';

import { HierarchyNode } from '®shared/hierarchy';
import {
  Permissions,
  UserPermissions,
  hasPermissionForListId,
} from '®shared/permissions';

import {
  HierarchyAndStudentNumbers,
  HierarchyAndStudentNumbersTransfer,
  NodeAndOverride,
  getStudentNumbersOrOverridden,
  getTotalStudentNumbers,
  normaliseHierarchyAndStudentNumbers,
} from '../common';

import {
  NodesEditorHideModalAction,
  NodesEditorNewListHideModalAction,
  NodesEditorNewListPersistNodesAction,
  NodesEditorSaveNodesAction,
} from './nodes-editor-modal.actions';
import { HierarchyEditorState } from './nodes-editor.state';

const MAX_STUDENT_NUMBERS = 9999;

const STUDENT_NUMBER_VALIDATORS = [
  Validators.pattern(/^[0-9]+$/),
  Validators.min(0),
  Validators.max(MAX_STUDENT_NUMBERS),
];

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'rl-nodes-editor',
  templateUrl: './nodes-editor.component.html',
})
export class NodesEditorComponent implements OnChanges {
  @Input() hierarchyEditor: HierarchyEditorState;
  @Input() permissions: UserPermissions;
  @Input() isHierarchyUpdateEnabled: boolean;
  @Input() isNewList: boolean;

  @Output() save = new EventEmitter<
    NodesEditorSaveNodesAction | NodesEditorNewListPersistNodesAction
  >();
  @Output() close = new EventEmitter<
    NodesEditorHideModalAction | NodesEditorNewListHideModalAction
  >();

  hierarchyAndStudentNumbersForm: UntypedFormGroup;

  constructor(private formBuilder: UntypedFormBuilder) {
    this.hierarchyAndStudentNumbersForm = new UntypedFormGroup({
      initialStatus: new UntypedFormControl(''),
      listStudentNumbers: new UntypedFormControl(''),
      nodesAndOverrides: new UntypedFormArray([]),
    });
  }

  get areFormInteractionsDisabled(): boolean {
    return !this.isHierarchyUpdateEnabled || this.hierarchyEditor.isSaving;
  }

  get listId(): string {
    return this.hierarchyEditor.listId;
  }

  get isDraft(): boolean {
    return this.hierarchyEditor.isDraft;
  }

  get hierarchyAndStudentNumbers(): HierarchyAndStudentNumbers {
    return this.hierarchyEditor.hierarchyAndStudentNumbers;
  }

  get hasAttachToHierarchyPermission(): boolean {
    const { listId = undefined } = this.hierarchyEditor || {};

    if (this.isNewList) {
      return this.permissions.hasPermission(Permissions.ATTACH_LIST_TO_NODE);
    }

    return (
      listId &&
      this.permissions &&
      hasPermissionForListId(
        Permissions.ATTACH_LIST_TO_NODE,
        listId,
        this.permissions,
      )
    );
  }

  get haveLegacyNumbersBeenTransferred(): boolean {
    const { initialStatus } = this.hierarchyAndStudentNumbers;
    return (
      initialStatus ===
      HierarchyAndStudentNumbersTransfer.FromLegacyToFirstExistingNode
    );
  }

  get willListBoundNumbersBeTransferred(): boolean {
    const { initialStatus } = this.hierarchyAndStudentNumbers;
    return (
      initialStatus ===
      HierarchyAndStudentNumbersTransfer.FromListToFirstNewlyAddedNode
    );
  }

  get listStudentNumbersFormControl(): UntypedFormControl {
    return this.hierarchyAndStudentNumbersForm.get(
      'listStudentNumbers',
    ) as UntypedFormControl;
  }

  get nodesAndOverridesFormArray(): UntypedFormArray {
    return this.hierarchyAndStudentNumbersForm.get(
      'nodesAndOverrides',
    ) as UntypedFormArray;
  }

  get nodesAndOverridesValues(): NodeAndOverride[] {
    return this.nodesAndOverridesFormArray.controls.map(ctrl => ctrl.value);
  }

  ngOnChanges(changes?: SimpleChanges) {
    if (this.hierarchyEditor.hierarchyAndStudentNumbers) {
      this.recreateForm();
    }

    if (
      changes &&
      changes.hierarchyEditor &&
      this.hierarchyAndStudentNumbersForm &&
      this.hierarchyAndStudentNumbersForm.disabled !==
        this.areFormInteractionsDisabled
    ) {
      if (this.areFormInteractionsDisabled) {
        this.hierarchyAndStudentNumbersForm.disable();
      } else {
        this.hierarchyAndStudentNumbersForm.enable();
      }
    }
  }

  private recreateForm() {
    this.hierarchyAndStudentNumbersForm.setControl(
      'initialStatus',
      this.buildInitialStatusControl(),
    );
    this.hierarchyAndStudentNumbersForm.setControl(
      'listStudentNumbers',
      this.buildListOnlyNumbersControl(),
    );
    this.hierarchyAndStudentNumbersForm.setControl(
      'nodesAndOverrides',
      this.buildNodeOverrideNumberControls(),
    );

    if (this.areFormInteractionsDisabled) {
      this.hierarchyAndStudentNumbersForm.disable();
    } else {
      this.hierarchyAndStudentNumbersForm.enable();
    }
  }

  onClose(): void {
    if (this.hierarchyEditor.isSaving) {
      return;
    }

    const action = this.isNewList
      ? new NodesEditorNewListHideModalAction()
      : new NodesEditorHideModalAction();

    this.close.emit(action);
  }

  onSubmit(): void {
    if (
      this.hierarchyEditor.isSaving ||
      !this.hierarchyAndStudentNumbersForm.valid
    ) {
      return;
    }

    // retain the original initialStatus value throughout
    const { initialStatus } = this.hierarchyAndStudentNumbers;
    const updatedModel = this.hierarchyAndStudentNumbersForm.value;
    const hierarchyAndStudentNumbers = normaliseHierarchyAndStudentNumbers({
      ...updatedModel,
      initialStatus,
    });

    const action = this.isNewList
      ? new NodesEditorNewListPersistNodesAction(hierarchyAndStudentNumbers)
      : new NodesEditorSaveNodesAction({
          hierarchyAndStudentNumbers,
          listId: this.listId,
          isDraft: this.isDraft,
        });

    this.save.emit(action);
  }

  private buildInitialStatusControl(): UntypedFormControl {
    const { initialStatus } = this.hierarchyAndStudentNumbers;
    return new UntypedFormControl(initialStatus);
  }

  private buildListOnlyNumbersControl(): UntypedFormControl {
    const { listStudentNumbers } = this.hierarchyAndStudentNumbers;
    return new UntypedFormControl(listStudentNumbers, {
      validators: STUDENT_NUMBER_VALIDATORS,
    });
  }

  private buildNodeOverrideNumberControls(): UntypedFormArray {
    const { nodesAndOverrides } = this.hierarchyAndStudentNumbers;
    const controlsForNodes = (nodesAndOverrides || []).map(nodeAndOverride =>
      this.getNodeAndStudentNumbersCtrl(nodeAndOverride),
    );
    return this.formBuilder.array(controlsForNodes);
  }

  addNode(node: HierarchyNode) {
    if (this.hierarchyEditor.isSaving) {
      return;
    }

    // do not add nodes that are already present
    if (isNodeAlreadyAttachedToList(node, this.nodesAndOverridesValues)) {
      return;
    }

    // the first time a node is attached, the list list student numbers should be transferred
    const { listStudentNumbers } = this.hierarchyAndStudentNumbers;
    const overriddenStudentNumbers =
      listStudentNumbers || node.student_numbers || 0;
    const nodeAndOverride: NodeAndOverride = { node, overriddenStudentNumbers };

    // clear the value of the list student numbers once transferred - ensures first time only
    this.listStudentNumbersFormControl.setValue(null, { emitEvent: false });

    this.nodesAndOverridesFormArray.push(
      this.getNodeAndStudentNumbersCtrl(nodeAndOverride),
    );
  }

  removeNode(index: number) {
    if (this.hierarchyEditor.isSaving) {
      return;
    }

    this.nodesAndOverridesFormArray.removeAt(index);
  }

  getNodeAndStudentNumbersCtrl(
    nodeAndOverride: NodeAndOverride,
  ): UntypedFormGroup {
    const nodeCtrl = this.formBuilder.control(nodeAndOverride.node);

    const currentNumbers = getStudentNumbersOrOverridden(nodeAndOverride);
    const overriddenStudentNumbersCtrl = new UntypedFormControl(
      currentNumbers,
      {
        validators: STUDENT_NUMBER_VALIDATORS,
      },
    );

    return this.formBuilder.group({
      node: nodeCtrl,
      overriddenStudentNumbers: overriddenStudentNumbersCtrl,
    });
  }

  get totalStudentNumbers(): number {
    if (!this.hierarchyAndStudentNumbersForm.valid) {
      return 0;
    }
    return getTotalStudentNumbers(
      this.hierarchyAndStudentNumbersForm.value as HierarchyAndStudentNumbers,
    );
  }
}

export function isNodeAlreadyAttachedToList(
  node: HierarchyNode,
  nodesAndOverrides: NodeAndOverride[] = [],
): boolean {
  const nodesAndOverridesList = List(nodesAndOverrides);
  const containsNode = (nodeAndOverride: NodeAndOverride) =>
    node.id === nodeAndOverride.node.id &&
    node.normalisedType === nodeAndOverride.node.normalisedType;
  return nodesAndOverridesList.find(containsNode) ? true : false;
}
